import { api } from "@services/api";
import { mapGatePasses } from "@utils/mappers/gatepassMapper";
import type {
  BulkCompleteGatepassRequest,
  BulkCompleteGatepassResponse,
  CompleteGatepassPayload,
  GatePassApiResponse,
  GatePassApiResponseItem,
  GatePassListItem,
} from "./gatepass/types";

interface GetGatePassParams {
  service: string;
  page?: number;
  size?: number;
  filters?: Record<string, any>;
}

export const getGatePasses = async ({
  service,
  page = 0,
  size = 4,
  filters = {},
}: GetGatePassParams): Promise<{
  gatepasses: GatePassListItem[];
  totalRecordCount: number;
}> => {
  const data: GatePassApiResponse = await api.post(
    service,
    `gatepass-requests/search?pageSize=${size}&pageNumber=${page}`,
    {
      ...filters,
    },
  );

  const mappedData = mapGatePasses(data.records);

  return {
    gatepasses: mappedData,
    totalRecordCount: data.totalRecordCount,
  };
};

export const getGatepassById = async (
  service: string,
  id: string,
): Promise<GatePassApiResponseItem> => {
  return api.get<GatePassApiResponseItem>(service, `gatepass-requests/${id}`);
};

export const claimGatepassById = async ({
  service,
  id,
  transactionId,
}: {
  service: string;
  id: string;
  transactionId?: string;
}) => {
  return api.put(
    service,
    `gatepass-requests/${id}:claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const unclaimGatepassById = async ({
  service,
  id,
  transactionId,
}: {
  service: string;
  id: string;
  transactionId?: string;
}) => {
  return api.put(
    service,
    `gatepass-requests/${id}:unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const completeGatepassById = async ({
  service,
  id,
  payload,
  transactionId,
}: {
  service: string;
  id: string;
  payload: CompleteGatepassPayload;
  transactionId?: string;
}) => {
  return api.put(
    service,
    `gatepass-requests/${id}:complete`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export type GatepassBulkTaskOperation = "CLAIM" | "UNCLAIM";

export const bulkUpdateGatepassTasks = async ({
  service,
  taskIds,
  taskOperation,
  transactionId,
}: {
  service: string;
  taskIds: string[];
  taskOperation: GatepassBulkTaskOperation;
  transactionId?: string;
}): Promise<BulkCompleteGatepassResponse> => {
  return api.put(
    service,
    "gatepass-requests/bulk",
    {
      taskIds,
      taskOperation,
    },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const bulkCompleteGatepass = async (
  service: string,
  payload: BulkCompleteGatepassRequest,
  transactionId?: string,
): Promise<BulkCompleteGatepassResponse> => {
  return api.put(
    service,
    "gatepass-requests/bulk:complete",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};
